import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { format } from 'date-fns';
import {
  Plus, Search, Loader2, X, Trash2, MoreHorizontal, Pencil, Filter, DollarSign, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Check', 'PayPal', 'Stripe', 'Other'];
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

// ---- Combobox ----
function ComboInput({ value, onChange, options, placeholder, testId, colorMap }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);
  const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase()));
  const isNew = query.trim() && !options.some(o => o.toLowerCase() === query.trim().toLowerCase());
  return (
    <div ref={wrapperRef} className="relative">
      <Input ref={inputRef} value={query} onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder={placeholder} data-testid={testId} className="w-full" autoComplete="off" />
      {open && (filtered.length > 0 || isNew) && (
        <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((opt, i) => (
            <button key={i} type="button" onPointerDown={(e) => { e.preventDefault(); onChange(opt); setQuery(opt); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
              {colorMap?.[opt] && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[opt] }} />}
              {opt}
            </button>
          ))}
          {isNew && (
            <button type="button" onPointerDown={(e) => { e.preventDefault(); onChange(query.trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-600 font-medium border-t border-gray-100 flex items-center gap-2"
              data-testid={`${testId}-create-new`}>
              <Plus className="w-3.5 h-3.5" />Create "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Revenue Dialog ----
function RevenueDialog({ open, onOpenChange, revenue, companyId, categories, onSave }) {
  const [form, setForm] = useState({
    description: '', amount: '', date: new Date().toISOString().slice(0, 10),
    category: '', subcategory: '', source: '', payment_method: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (revenue) {
        setForm({
          description: revenue.description || '', amount: String(revenue.amount || ''),
          date: revenue.date || new Date().toISOString().slice(0, 10),
          category: revenue.category || '', subcategory: revenue.subcategory || '',
          source: revenue.source || '', payment_method: revenue.payment_method || '',
          notes: revenue.notes || ''
        });
      } else {
        setForm({ description: '', amount: '', date: new Date().toISOString().slice(0, 10),
          category: '', subcategory: '', source: '', payment_method: '', notes: '' });
      }
    }
  }, [revenue, open]);

  const catOptions = categories.map(c => c.name);
  const colorMap = Object.fromEntries(categories.map(c => [c.name, c.color]));
  const selectedCat = categories.find(c => c.name === form.category);
  const subOptions = selectedCat?.subcategories || [];

  const handleSubmit = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form, amount: parseFloat(form.amount) || 0, company_id: companyId,
        subcategory: form.subcategory || null, source: form.source || null,
        payment_method: form.payment_method || null,
      };
      if (revenue) {
        await axios.put(`${API_URL}/api/revenue/${revenue.id}`, payload, { withCredentials: true });
        toast.success('Revenue updated');
      } else {
        await axios.post(`${API_URL}/api/revenue`, payload, { withCredentials: true });
        toast.success('Revenue added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{revenue ? 'Edit Revenue' : 'Add Revenue'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Description *</Label>
            <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
              placeholder="Revenue description" data-testid="revenue-description-input" />
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))}
              placeholder="0.00" data-testid="revenue-amount-input" />
          </div>
          <div>
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="revenue-date-btn">
                  {form.date || 'Pick date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[100]" align="start">
                <Calendar mode="single" selected={form.date ? new Date(form.date + 'T12:00:00') : undefined}
                  onSelect={d => { if (d) setForm(p => ({...p, date: format(d, 'yyyy-MM-dd')})); }} />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Category</Label>
            <ComboInput value={form.category} onChange={v => setForm(p => ({...p, category: v, subcategory: ''}))}
              options={catOptions} placeholder="Select or type category" testId="revenue-category-input" colorMap={colorMap} />
          </div>
          <div>
            <Label>Subcategory</Label>
            <ComboInput value={form.subcategory} onChange={v => setForm(p => ({...p, subcategory: v}))}
              options={subOptions} placeholder="Select or type" testId="revenue-subcategory-input" />
          </div>
          <div>
            <Label>Source</Label>
            <Input value={form.source} onChange={e => setForm(p => ({...p, source: e.target.value}))}
              placeholder="e.g., Client, Online Store" data-testid="revenue-source-input" />
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => setForm(p => ({...p, payment_method: v}))}>
              <SelectTrigger data-testid="revenue-payment-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="revenue-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-revenue-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : revenue ? 'Update' : 'Add Revenue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Main Page ----
export default function RevenuePage() {
  const { currentCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/revenue/export/csv',
    importUrl: '/api/revenue/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchAll(),
    entityName: 'revenue'
  });

  const fetchItems = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/revenue`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setItems(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  const fetchCategories = useCallback(async () => {
    if (!currentCompany) return;
    try {
      const res = await axios.get(`${API_URL}/api/revenue-categories`, { params: { company_id: currentCompany.id }, withCredentials: true });
      setCategories(res.data);
    } catch { /* ignore */ }
  }, [currentCompany]);

  const fetchAll = useCallback(() => { fetchItems(); fetchCategories(); }, [fetchItems, fetchCategories]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/revenue/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Revenue entry deleted');
      setDeleteTarget(null); fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${API_URL}/api/revenue/bulk-delete`, { ids: selected }, { withCredentials: true });
      toast.success(`Deleted ${selected.length} entries`);
      setSelected([]); fetchItems();
    } catch { toast.error('Bulk delete failed'); }
  };

  const [pdfImporting, setPdfImporting] = useState(false);
  const pdfInputRef = useRef(null);
  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files supported'); return; }
    setPdfImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('company_id', currentCompany.id);
      const res = await axios.post(`${API_URL}/api/revenue/import/pdf?company_id=${currentCompany.id}`, formData, { withCredentials: true });
      toast.success(`Imported ${res.data.imported} revenue entries`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} rows had errors`);
      fetchAll();
    } catch { toast.error('PDF import failed'); }
    finally { setPdfImporting(false); if (pdfInputRef.current) pdfInputRef.current.value = ''; }
  };

  // Filters
  const uniqueCats = [...new Set(items.map(r => r.category).filter(Boolean))];
  const catCounts = {};
  items.forEach(r => { catCounts[r.category] = (catCounts[r.category] || 0) + 1; });

  const filtered = items.filter(r => {
    if (catFilter !== 'all' && r.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (r.description || '').toLowerCase().includes(q) ||
             (r.source || '').toLowerCase().includes(q) ||
             (r.notes || '').toLowerCase().includes(q);
    }
    return true;
  });

  const hasFilters = catFilter !== 'all' || search;
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map(r => r.id));
  };

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="revenue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Revenue</h1>
          <p className="text-gray-500 text-sm mt-0.5">{currentCompany.name} revenue entries</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="revenue" />
          <input type="file" ref={pdfInputRef} accept=".pdf" className="hidden" onChange={handlePdfImport} data-testid="revenue-pdf-input" />
          <Button variant="outline" onClick={() => pdfInputRef.current?.click()} disabled={pdfImporting} data-testid="revenue-import-pdf-btn">
            {pdfImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Import PDF
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="add-revenue-btn">
            <Plus className="w-4 h-4 mr-2" />Add Revenue
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search revenue..."
          className="pl-9" data-testid="revenue-search" />
        {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-gray-400" /></button>}
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setCatFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${catFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="rev-cat-filter-all">
          All ({items.length})
        </button>
        {uniqueCats.map(cat => (
          <button key={cat} onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${catFilter === cat ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
            data-testid={`rev-cat-filter-${cat.toLowerCase().replace(/\s/g,'-')}`}>
            {cat} ({catCounts[cat] || 0})
          </button>
        ))}
        {hasFilters && (
          <button onClick={() => { setCatFilter('all'); setSearch(''); }} className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-2.5 border border-blue-200" data-testid="revenue-bulk-actions">
          <span className="text-sm text-blue-900 font-medium">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkDeleteConfirm(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="revenue-bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete Selected
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected([])}>Clear</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="w-10"><Checkbox checked={filtered.length > 0 && selected.length === filtered.length} onCheckedChange={toggleAll} /></TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">
                <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="font-medium">No revenue entries yet</p>
                <p className="text-xs mt-1">Click "Add Revenue" to start tracking income</p>
              </TableCell></TableRow>
            ) : filtered.map(r => (
              <TableRow key={r.id} className="hover:bg-gray-50/50" data-testid={`revenue-row-${r.id}`}>
                <TableCell><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleSelect(r.id)} /></TableCell>
                <TableCell className="text-sm text-gray-500 whitespace-nowrap">{r.date}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-gray-900">{r.description}</div>
                  {r.notes && <p className="text-xs text-gray-400 truncate max-w-[200px]">{r.notes}</p>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {r.category || 'Uncategorized'}
                  </span>
                  {r.subcategory && <span className="block text-[10px] text-gray-400 mt-0.5">{r.subcategory}</span>}
                </TableCell>
                <TableCell className="text-sm text-gray-500">{r.source || '-'}</TableCell>
                <TableCell className="text-right font-semibold text-blue-700">{fmtCurrency(r.amount)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditItem(r)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteTarget(r)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Count */}
      <div className="flex justify-end px-2">
        <span className="text-sm text-gray-500">
          {filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}
          {hasFilters && ` (of ${items.length})`}
        </span>
      </div>

      {/* Dialogs */}
      <RevenueDialog open={addOpen || !!editItem} onOpenChange={v => { if (!v) { setAddOpen(false); setEditItem(null); } }}
        revenue={editItem} companyId={currentCompany?.id} categories={categories} onSave={fetchAll} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Revenue Entry</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.description}"? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} Revenue Entr{selected.length !== 1 ? 'ies' : 'y'}?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete {selected.length} selected entr{selected.length !== 1 ? 'ies' : 'y'}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setBulkDeleteConfirm(false); handleBulkDelete(); }} className="bg-red-600 hover:bg-red-700">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
